import React from "react";
import { api } from "../../services/api";
import { Plus, Trash2, Pencil, X, Check } from "lucide-react";

const inputCls =
  "px-3 py-2 rounded-lg border border-gray-300 outline-none focus:border-[#e8491d] focus:ring-2 focus:ring-[#e8491d]/30 text-sm";

const emptyForm = { id: null, city: "SUNDAY TEER", dateObj: "", frTime: "4:30 PM", srTime: "5:30 PM", fr: "", sr: "" };

export default function ResultsAdmin({ onAuthError }) {
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [form, setForm] = React.useState(emptyForm);
  const [msg, setMsg] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback((append = false, skip = 0) => {
    api.results({ limit: 40, skip }).then((data) => {
      setTotal(data.total);
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
    });
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleErr = (e) => {
    if (e?.response?.status === 401) onAuthError();
    else setMsg("Error: " + (e?.response?.data?.detail || "failed"));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.dateObj) return setMsg("Please pick a date");
    setSaving(true);
    setMsg("");
    try {
      const body = { city: form.city, dateObj: form.dateObj, frTime: form.frTime, srTime: form.srTime, fr: form.fr, sr: form.sr };
      if (form.id) await api.updateResult(form.id, body);
      else await api.createResult(body);
      setForm(emptyForm);
      setMsg("Saved! It now shows on Home & Previous Result.");
      load();
    } catch (e2) {
      handleErr(e2);
    } finally {
      setSaving(false);
    }
  };

  const edit = (r) => {
    setForm({ id: r.id, city: r.city, dateObj: r.dateObj, frTime: r.frTime, srTime: r.srTime, fr: r.fr, sr: r.sr });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this result?")) return;
    try {
      await api.deleteResult(id);
      load();
    } catch (e) {
      handleErr(e);
    }
  };

  return (
    <div>
      <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5">
        <h3 className="font-black text-[#1f2733] mb-3">{form.id ? "Edit Result" : "Add Result"}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="flex flex-col">
            <label className="text-xs font-bold text-gray-500 mb-1">Date</label>
            <input type="date" value={form.dateObj} onChange={(e) => setForm({ ...form, dateObj: e.target.value })} className={inputCls} />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-bold text-gray-500 mb-1">City</label>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-bold text-gray-500 mb-1">F/R Number</label>
            <input value={form.fr} onChange={(e) => setForm({ ...form, fr: e.target.value })} className={inputCls} placeholder="e.g. 67" />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-bold text-gray-500 mb-1">S/R Number</label>
            <input value={form.sr} onChange={(e) => setForm({ ...form, sr: e.target.value })} className={inputCls} placeholder="e.g. 54" />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-bold text-gray-500 mb-1">F/R Time</label>
            <input value={form.frTime} onChange={(e) => setForm({ ...form, frTime: e.target.value })} className={inputCls} />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-bold text-gray-500 mb-1">S/R Time</label>
            <input value={form.srTime} onChange={(e) => setForm({ ...form, srTime: e.target.value })} className={inputCls} />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button disabled={saving} className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#e8491d] text-white font-bold hover:bg-[#c53a12] transition-colors disabled:opacity-60">
            {form.id ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {form.id ? "Update" : "Add"}
          </button>
          {form.id && (
            <button type="button" onClick={() => setForm(emptyForm)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-bold hover:bg-gray-200">
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
          {msg && <span className="text-sm text-gray-600">{msg}</span>}
        </div>
      </form>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="grid grid-cols-5 bg-gray-100 font-bold text-sm border-b border-gray-200">
          <div className="py-2.5 px-3">Date</div>
          <div className="py-2.5 px-3">City</div>
          <div className="py-2.5 px-3 text-center">F/R</div>
          <div className="py-2.5 px-3 text-center">S/R</div>
          <div className="py-2.5 px-3 text-center">Actions</div>
        </div>
        {items.map((r, i) => (
          <div key={r.id} className={`grid grid-cols-5 text-sm border-b border-gray-100 items-center ${i % 2 ? "bg-gray-50" : "bg-white"}`}>
            <div className="py-2 px-3">{r.date}</div>
            <div className="py-2 px-3">{r.city}</div>
            <div className="py-2 px-3 text-center font-bold">{r.fr}</div>
            <div className="py-2 px-3 text-center font-bold">{r.sr}</div>
            <div className="py-2 px-3 flex items-center justify-center gap-2">
              <button onClick={() => edit(r)} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(r.id)} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {items.length < total && (
        <div className="text-center mt-4">
          <button onClick={() => load(true, items.length)} className="px-5 py-2 rounded-xl bg-gray-100 font-bold hover:bg-gray-200 text-sm">
            Load More ({total - items.length} left)
          </button>
        </div>
      )}
    </div>
  );
}
